import React from 'react';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import ImageIcon from '@mui/icons-material/Image';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import styles from './FileListingArea.module.css';

const getFileIcon = (fileName, mimeType) => {
  // Check mimeType first (for Google Drive files)
  if (mimeType) {
    if (mimeType.startsWith('video/')) {
      return <VideoLibraryIcon />;
    }
    if (mimeType.startsWith('image/')) {
      return <ImageIcon />;
    }
  }

  // Fallback to extension check (for Dropbox and other sources)
  const extension = fileName.split('.').pop().toLowerCase();

  if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(extension)) {
    return <VideoLibraryIcon />;
  }
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
    return <ImageIcon />;
  }
  return <InsertDriveFileIcon />;
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return 'Unknown size';

  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${Math.round((bytes / (1024 ** i)) * 100) / 100} ${sizes[i]}`;
};

export const FileListingArea = ({ files, connectedServices, onImport, onRemoveFile }) => {
  const hasConnectedService = Object.values(connectedServices).some((service) => service !== null);
  const hasDropbox = connectedServices.dropbox !== null;
  const hasGoogleDrive = connectedServices.googledrive !== null;

  if (files.length === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>
          <FolderOpenIcon style={{ fontSize: 64 }} />
        </div>
        <h3 className={styles.emptyTitle}>No files selected</h3>
        <p className={styles.emptyDescription}>
          {hasConnectedService ? (
            'Import files from your connected cloud services to get started'
          ) : (
            'Connect a cloud service first to import files'
          )}
        </p>
        {hasConnectedService && (
          <div className={styles.importButtonsContainer}>
            {hasDropbox && (
              <button
                className={styles.importButton}
                onClick={() => onImport('dropbox')}
              >
                <CloudUploadIcon style={{ marginRight: 8 }} />
                Import from Dropbox
              </button>
            )}
            {hasGoogleDrive && (
              <button
                className={styles.importButton}
                onClick={() => onImport('googledrive')}
              >
                <CloudUploadIcon style={{ marginRight: 8 }} />
                Import from Google Drive
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.fileList}>
        {files.map((file) => (
          <div key={file.id} className={styles.fileCard}>
            <div className={styles.fileIcon}>
              {getFileIcon(file.name, file.mimeType)}
            </div>

            <div className={styles.fileInfo}>
              <h4 className={styles.fileName}>{file.name}</h4>
              <div className={styles.fileMetadata}>
                <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
                <span className={styles.fileDivider}>•</span>
                <span className={styles.fileSource}>
                  {file.source.charAt(0).toUpperCase() + file.source.slice(1)}
                </span>
              </div>
            </div>

            <button
              className={styles.removeButton}
              onClick={() => onRemoveFile(file.id)}
              aria-label="Remove file"
            >
              <DeleteIcon style={{ fontSize: 18 }} />
            </button>
          </div>
        ))}
      </div>

      {hasConnectedService && (
        <div className={styles.addMoreSection}>
          {hasDropbox && (
            <button
              className={styles.addMoreButton}
              onClick={() => onImport('dropbox')}
            >
              <CloudUploadIcon style={{ marginRight: 8 }} />
              Add from Dropbox
            </button>
          )}
          {hasGoogleDrive && (
            <button
              className={styles.addMoreButton}
              onClick={() => onImport('googledrive')}
            >
              <CloudUploadIcon style={{ marginRight: 8 }} />
              Add from Google Drive
            </button>
          )}
        </div>
      )}
    </div>
  );
};
